/**
 * @file file-preview.ts
 * @description 統一的「已上傳檔案預覽 / 下載」工具。
 *
 * 配合後端 `GET /api/files/:id/download?disposition=inline|attachment`：
 * - `previewFile`  以 inline disposition 開新分頁讓瀏覽器原生預覽
 *                  （PDF / 圖片直接顯示；Office 文件改走 `/:id/preview.pdf` 轉檔後預覽）
 * - `downloadFile` 以 attachment disposition 強制觸發下載
 *
 * 為何不在前端對 PDF / 圖片做格式判斷：
 * - 瀏覽器對「能否預覽」的判定比 mimeType 白名單更可靠（PDF.js、原生圖片、SVG 均能直顯）
 * - 真的不能預覽的情況下，瀏覽器會 fallback 為下載，使用者體驗不會比硬下載差
 *
 * Office 文件（doc/docx/xls/xlsx/ppt/pptx）例外：瀏覽器無法原生渲染，一律觸發下載，
 * 因此需要靠檔名副檔名（`getOfficePreviewExtension`）主動分流到後端轉 PDF 端點。
 */

import { getOfficePreviewExtension } from '@zhcb/shared';
import { message } from '@/lib/antd-app';
import { apiClient } from '@/lib/api-client';

/** 後端 `/api/files/:id/download` 的回傳結構（包成 successResponse 的 data 欄位） */
interface PresignedDownload {
  /** 預簽名 URL（5 分鐘有效） */
  url: string;
  /** 有效期（秒） */
  expiresInSec: number;
}

/**
 * 取得指定 file 的預簽名 URL。
 *
 * @param fileId       資料庫 files.id
 * @param disposition  'inline'（預覽）| 'attachment'（下載），預設 inline
 */
async function fetchPresignedUrl(
  fileId: number,
  disposition: 'inline' | 'attachment' = 'inline',
): Promise<string> {
  const res = await apiClient.get<PresignedDownload>(
    `/api/files/${fileId}/download?disposition=${disposition}`,
  );
  return res.url;
}

/**
 * 在新分頁預覽指定檔案。
 *
 * 流程：
 * - Office 文件（依 `filename` 副檔名判斷）→ 直接開後端轉檔端點
 *   `GET /api/files/:id/preview.pdf`（LibreOffice headless 即時轉 PDF，見
 *   `apps/api/src/routes/files.ts`），瀏覽器原生 PDF viewer 顯示；此端點走既有
 *   httpOnly cookie 認證，不需要先取預簽名 URL。
 * - 其餘類型（PDF / 圖片等）→ 取得 inline 模式的 OSS 預簽名 URL → `window.open`
 *   新分頁，由瀏覽器原生渲染。
 *
 * 失敗時用 antd `message.error` 提示，並把錯誤輸出到 console 便於排查（OSS / 權限 / 過期）。
 *
 * @param fileId   資料庫 files.id；未提供則直接提示「無附件」。
 * @param filename 原始檔名（含副檔名），用於判斷是否需要轉 PDF 預覽；未提供則視為非 Office 文件。
 */
export async function previewFile(
  fileId: number | null | undefined,
  filename?: string,
): Promise<void> {
  if (!fileId) {
    message.warning('沒有可預覽的附件');
    return;
  }
  try {
    if (getOfficePreviewExtension(filename)) {
      // 新分頁打開；noopener 避免新頁面拿到 window.opener 的引用造成 XSS / phishing 風險
      window.open(`/api/files/${fileId}/preview.pdf`, '_blank', 'noopener,noreferrer');
      return;
    }
    const url = await fetchPresignedUrl(fileId, 'inline');
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    // 業務必要：取得預簽名 URL 失敗需要記錯誤日誌，便於使用者反饋時還原 OSS / 權限狀態
    console.error('[file-preview] previewFile failed', { fileId, err });
    message.error(err instanceof Error ? err.message : '無法載入預覽');
  }
}

/**
 * 強制下載指定檔案。
 *
 * attachment 模式下 OSS 會在 Content-Disposition 帶上原始檔名，
 * 瀏覽器會自動觸發下載並使用該檔名儲存。
 *
 * @param fileId 資料庫 files.id
 */
export async function downloadFile(fileId: number | null | undefined): Promise<void> {
  if (!fileId) {
    message.warning('沒有可下載的附件');
    return;
  }
  try {
    const url = await fetchPresignedUrl(fileId, 'attachment');
    // attachment header 會觸發下載；window.open 不會留下空白分頁（瀏覽器發現是下載即關閉）
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    console.error('[file-preview] downloadFile failed', { fileId, err });
    message.error(err instanceof Error ? err.message : '無法下載檔案');
  }
}
